import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Check, X } from "react-feather";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import styled from "@emotion/styled";

import StyledFormInput from "./StyledFormInput";
import FormHelper from "../../utils/FormHelper";
import { FiLock } from "react-icons/fi";

const passwordTips = {
  CHARS: "No mínimo {QTY} caracteres.",
  LOWERCASE: "No mínimo {QTY}  letra maiúscula (A-Z)",
  UPPERCASE: "No mínimo {QTY} letra minúscula (a-z)",
  NUMBER: "No mínimo {QTY} número (0-9)",
  SYMBOL: "No mínimo {QTY} símbolo",
};

const FormInput = forwardRef((props, ref) => {
  const [typeEye, setTypeEye] = useState("open");
  const { input, layout, config, styleLabel, styleError } = props;
  const [rand, setRand] = useState();
  const [inputType, setInputType] = useState(input?.type);

  useEffect(() => {
    if (input?.startValue) {
      input.inputRef.current.value = input.startValue;
      onChange();
    }
  }, [input.startValue]);

  useImperativeHandle(ref, () => ({
    setValue(value) {
      input.inputRef.current.value = value;
    },
    reset() {
      if (!input.inputOptions?.disabled && !input.inputOptions?.readonly) {
        input.setValue("");
      }
    },
  }));

  const maskDate = (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1/$2")
      .replace(/(\d{2})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1");

  const maskPhone = (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d{4})(\d)*/, "$1-$2$3")
      .replace(/(\d{4})-(\d{1})(\d{4})(\d)*/, "$1$2-$3");

  const maskCPF = (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");

  const maskCNPJ = (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");

  const maskCEP = (value) =>
    value.replace(/\D/g, "").replace(/^(\d{5})(\d{3})+?$/, "$1-$2");

  const maskLetters = (value) =>
    value.replace(/[0-9`´!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]+/g, "");

  const maskUsername = (value) => value.replace(/[^a-zA-Z0-9@._-]+/g, "");

  const maskAlphanumeric = (value) =>
    value.replace(/[`´!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]+/g, "");

  const maskEmail = (value) => value.replace(/[^a-zA-Z0-9@._\-+]+/g, "");

  const maskNumbers = (value) => value.replace(/\D/g, "");

  const maskUseroptions = (value) => {
    let maskOptions = input?.maskOptions || [];
    let ncount = value.replace(/\D/g, "").length;
    if (
      // Se só tem número e \ e . e -
      /^[0-9\.\-\/]+$/g.test(value) ||
      // Ou se só o último char for letra
      (/^[0-9\.\-\/]+$/g.test(value.slice(0, -1)) &&
        /^[a-zA-Z]+$/g.test(value.slice(-1)))
    ) {
      // Neste caso pode ser CNPJ
      if (
        maskOptions.includes("cnpj") &&
        // Se não tem cpf nem text, é sempre cnpj
        ((!maskOptions.includes("cpf") && !maskOptions.includes("text")) ||
          // Se também tem text e não tem cpf, espera pelo menos ter 2 caracteres
          (!maskOptions.includes("cpf") &&
            maskOptions.includes("text") &&
            ncount >= 2) ||
          // Se também tem cpf, só é cnpj depois que passa da qtd de caracteres de cpf (15)
          (maskOptions.includes("cpf") && ncount >= 12))
      ) {
        return maskCNPJ(value);
      }
      // Ou CPF
      else if (maskOptions.includes("cpf") && ncount >= 3) {
        return maskCPF(value);
      }
    }

    // return maskUsername(value);
    return maskOptions.includes("text") ? maskUsername(value) : maskCNPJ(value);
  };

  const maskReal = (value) => {
    const valor = value.replace(/[^\d]+/gi, "");
    const parteInteira = valor.slice(0, -2);
    const parteDecimal = valor.slice(-2);

    let resultado = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    if (resultado.length > 0) {
      resultado += ",";
    }
    resultado += parteDecimal;

    if (value === "") {
      resultado = "";
    }
    return resultado;
  };

  const handleShowPassword = () => {
    if (inputType === "password") {
      setInputType("text");
      setTypeEye("close");
    } else {
      setInputType("password");
      setTypeEye("open");
    }
  };

  const onChange = () => {
    setRand(Math.random());
    if (input?.mask) {
      let value = input.inputRef.current.value;
      let maskF;
      switch (input.mask) {
        case "phone":
          maskF = maskPhone;
          break;
        case "date":
          maskF = maskDate;
          break;
        case "cpf":
          maskF = maskCPF;
          break;
        case "cep":
          maskF = maskCEP;
          break;
        case "letter":
          maskF = maskLetters;
          break;
        case "number":
          maskF = maskNumbers;
          break;
        case "username":
          maskF = maskUseroptions;
          break;
        case "alphanumeric":
          maskF = maskAlphanumeric;
          break;
        case "email":
          maskF = maskEmail;
          break;
        case "real":
          maskF = maskReal;
          break;
        default:
          maskF = null;
      }

      if (maskF) {
        input.inputRef.current.value = maskF(value);
      }
    }

    if (input.onChange) {
      input.onChange(input.inputRef.current.value, input.inputRef.current);
    }
  };

  return (
    <StyledFormInput
      {...props}
      config={config}
      className={`input__group ${layout} ${input?.type}-input`}
    >
      <input
        style={props.style}
        ref={input?.inputRef}
        onInput={onChange}
        type={inputType}
        {...input.inputOptions}
        disabled={input?.inputOptions?.disabled}
      />
      {input.type === "password" && (
        <span className="eye" onClick={handleShowPassword}>
          {typeEye === "open" ? (
            <FaRegEye size={24} />
          ) : (
            <FaRegEyeSlash size={24} />
          )}
        </span>
      )}
      {input?.inputOptions?.disabled && (
        <span className="eye">{<FiLock size={24} />}</span>
      )}
      <label style={styleLabel || {}}>
        <span className={"label-show"}>{input?.label || "Input"}</span>
        <span className={"label-placeholder"}>{input?.label || "Input"}</span>
      </label>
      {input?.error ? (
        <span
          typo="caption-md"
          style={styleError || {}}
          className="error"
          dangerouslySetInnerHTML={{ __html: input?.error }}
        />
      ) : (
        <></>
      )}
      {input?.append && <div className="append">{input?.append}</div>}
      {input.contentTips ? (
        <FormInputContentTips config={config} input={input} rand={rand} />
      ) : (
        <></>
      )}
    </StyledFormInput>
  );
});

export default FormInput;

const FormInputContentTips = (props) => {
  let { config, input, rand } = props;

  return (
    <StyledFormInputContentTips config={config}>
      {input.contentTips?.map((tip, index) => (
        <ContentTip
          key={index}
          {...props}
          rand={rand}
          tip={tip}
          value={input?.getValue()}
        />
      ))}
    </StyledFormInputContentTips>
  );
};

const ContentTip = (props) => {
  let { config, input, tip, value, rand } = props;
  let [phrase, setPhrase] = useState("");
  let [type, setType] = useState(tip.split(":")[0]);
  let [qty, setQty] = useState(tip.split(":")[1]);
  let [isSatisfied, setIsSatisfied] = useState(false);

  useEffect(() => {
    let phrase = passwordTips[type?.toUpperCase()];

    if (phrase && qty) {
      phrase = phrase.replace("{QTY}", qty);
    }

    setPhrase(phrase);
  }, [type, qty]);

  useEffect(() => {
    setIsSatisfied(
      FormHelper.validatePasswordCompositionItem(value, type, qty)
    );
  }, [rand]);

  return (
    <StyledContentTip config={config} isSatisfied={isSatisfied}>
      {isSatisfied ? (
        <Check size={16} className={"tip-icon tip-check"} />
      ) : (
        <X size={16} className={"tip-icon tip-x"} />
      )}{" "}
      {phrase}
    </StyledContentTip>
  );
};

const StyledFormInputContentTips = styled.div`
  margin-top: 10px;
`;

const StyledContentTip = styled.div`
  display: flex;
  flex-direction: center;
  align-items: center;
  margin-top: 2px;
  font-size: 0.9em;
  font-family: "Roboto";
  color: ${(props) => (props.isSatisfied ? "#AAA" : "#333")};

  .tip-icon {
    margin-right: 10px;

    &.tip-check {
      color: green;
    }
    &.tip-x {
      color: red;
    }
  }
`;
